import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './Auth.css';

const SignUp = ({ onSwitchToLogin }) => {
    const { signup, confirmSignup, resendConfirmationCode, loading, error, clearError } = useAuth();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
    });
    const [verificationData, setVerificationData] = useState({
        email: '',
        confirmationCode: '',
    });
    const [showVerificationForm, setShowVerificationForm] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [isSignupComplete, setIsSignupComplete] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear field error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleVerificationChange = (e) => {
        const { name, value } = e.target;
        setVerificationData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear field error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateSignupForm = () => {
        const errors = {};
        
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email is invalid';
        }
        
        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/.test(formData.password)) {
            errors.password = 'Password must contain uppercase, lowercase, number and special character';
        }
        
        if (!formData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }
        
        if (!formData.firstName.trim()) {
            errors.firstName = 'First name is required';
        }
        
        if (!formData.lastName.trim()) {
            errors.lastName = 'Last name is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateVerificationForm = () => {
        const errors = {};
        
        if (!verificationData.confirmationCode.trim()) {
            errors.confirmationCode = 'Verification code is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSignupSubmit = async (e) => {
        e.preventDefault();
        clearError();
        
        if (!validateSignupForm()) {
            return;
        }

        try {
            const attributes = {
                given_name: formData.firstName,
                family_name: formData.lastName,
            };

            await signup(formData.email, formData.password, attributes);
            setShowVerificationForm(true);
            setVerificationData(prev => ({ ...prev, email: formData.email }));
        } catch (err) {
            console.error('Signup error:', err);
        }
    };

    const handleVerificationSubmit = async (e) => {
        e.preventDefault();
        clearError();
        
        if (!validateVerificationForm()) {
            return;
        }

        try {
            await confirmSignup(verificationData.email, verificationData.confirmationCode);
            setIsSignupComplete(true);
        } catch (err) {
            console.error('Verification error:', err);
        }
    };

    const handleResendCode = async () => {
        try {
            clearError();
            await resendConfirmationCode(verificationData.email);
            alert('Verification code sent to your email!');
        } catch (err) {
            console.error('Resend code error:', err);
        }
    };

    if (isSignupComplete) {
        return (
            <div className="auth-container">
                <div className="auth-form">
                    <h2>Account Verified!</h2>
                    <p className="auth-subtitle">Your account has been successfully verified. You can now sign in.</p>
                    
                    <button 
                        type="button" 
                        className="auth-button"
                        onClick={onSwitchToLogin}
                    >
                        Sign In
                    </button>
                </div>
            </div>
        );
    }

    if (showVerificationForm) {
        return (
            <div className="auth-container">
                <div className="auth-form">
                    <h2>Verify Your Email</h2>
                    <p className="auth-subtitle">
                        We've sent a verification code to <strong>{verificationData.email}</strong>. 
                        Please enter the code below.
                    </p>
                    
                    {error && <div className="error-message">{error}</div>}
                    
                    <form onSubmit={handleVerificationSubmit}>
                        <div className="form-group">
                            <label htmlFor="confirmationCode">Verification Code</label>
                            <input
                                type="text"
                                id="confirmationCode"
                                name="confirmationCode"
                                value={verificationData.confirmationCode}
                                onChange={handleVerificationChange}
                                className={formErrors.confirmationCode ? 'error' : ''}
                                placeholder="Enter verification code"
                                maxLength="6"
                            />
                            {formErrors.confirmationCode && <span className="field-error">{formErrors.confirmationCode}</span>}
                        </div>

                        <button 
                            type="submit" 
                            className="auth-button"
                            disabled={loading}
                        >
                            {loading ? 'Verifying...' : 'Verify Email'}
                        </button>
                    </form>

                    <div className="auth-links">
                        <span>Didn't receive the code? </span>
                        <button 
                            type="button" 
                            className="link-button"
                            onClick={handleResendCode}
                            disabled={loading}
                        >
                            Resend Code
                        </button>
                    </div>

                    <div className="auth-links">
                        <button 
                            type="button" 
                            className="link-button"
                            onClick={onSwitchToLogin}
                        >
                            Back to Sign In
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="auth-form">
                <h2>Create Account</h2>
                <p className="auth-subtitle">Sign up for a new account</p>
                
                {error && <div className="error-message">{error}</div>}
                
                <form onSubmit={handleSignupSubmit}>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="firstName">First Name</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                className={formErrors.firstName ? 'error' : ''}
                                placeholder="First name"
                            />
                            {formErrors.firstName && <span className="field-error">{formErrors.firstName}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="lastName">Last Name</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                className={formErrors.lastName ? 'error' : ''}
                                placeholder="Last name"
                            />
                            {formErrors.lastName && <span className="field-error">{formErrors.lastName}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={formErrors.email ? 'error' : ''}
                            placeholder="Enter your email"
                        />
                        {formErrors.email && <span className="field-error">{formErrors.email}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className={formErrors.password ? 'error' : ''}
                            placeholder="Enter your password"
                        />
                        {formErrors.password && <span className="field-error">{formErrors.password}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            className={formErrors.confirmPassword ? 'error' : ''}
                            placeholder="Confirm your password"
                        />
                        {formErrors.confirmPassword && <span className="field-error">{formErrors.confirmPassword}</span>}
                    </div>

                    <button 
                        type="submit" 
                        className="auth-button"
                        disabled={loading}
                    >
                        {loading ? 'Creating Account...' : 'Create Account'}
                    </button>
                </form>

                <div className="auth-links">
                    <span>Already have an account? </span>
                    <button 
                        type="button" 
                        className="link-button"
                        onClick={onSwitchToLogin}
                    >
                        Sign In
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SignUp; 